/**
 * UI store — ephemeral client state: toasts, open modals, panels.
 *
 * Not persisted — this is about "what's showing right now".
 */

import { create } from "zustand";

export type ToastLevel = "info" | "success" | "warning" | "error";

export type Toast = {
  id: string;
  message: string;
  level: ToastLevel;
  description?: string;
  durationMs: number;
};

type UIState = {
  toasts: Toast[];
  addToast: (
    message: string,
    opts?: { level?: ToastLevel; description?: string; durationMs?: number },
  ) => string;
  removeToast: (id: string) => void;

  turnSummaryOpen: boolean;
  setTurnSummaryOpen: (open: boolean) => void;
};

let toastCounter = 0;

export const useUIStore = create<UIState>((set, get) => ({
  toasts: [],
  addToast: (message, opts) => {
    const id = `toast-${++toastCounter}`;
    const toast: Toast = {
      id,
      message,
      level: opts?.level ?? "info",
      durationMs: opts?.durationMs ?? (opts?.level === "error" ? 6000 : 4000),
      ...(opts?.description !== undefined ? { description: opts.description } : {}),
    };
    set({ toasts: [...get().toasts, toast] });
    window.setTimeout(() => get().removeToast(id), toast.durationMs);
    return id;
  },
  removeToast: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),

  turnSummaryOpen: false,
  setTurnSummaryOpen: (open) => set({ turnSummaryOpen: open }),
}));

/** Convenience hooks for common toast levels. */
export function useToast() {
  const addToast = useUIStore((s) => s.addToast);
  const emit = (level: ToastLevel) => (msg: string, description?: string) =>
    addToast(msg, description === undefined ? { level } : { level, description });
  return {
    success: emit("success"),
    error: emit("error"),
    info: emit("info"),
    warning: emit("warning"),
  };
}
